import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { ErrorBoundary } from '@/components';
import { DonationPopup } from '@/components/features';
import { ProtectedAdminRoute } from '@/components/ProtectedAdminRoute';
import { ScrollToTop } from '@/components/layout';
import { AuthProvider } from '@/context/AuthContext';
import { Home } from '@/pages/Home';
const About = lazy(() => import('@/pages/About').then(m => ({ default: m.About })));
const Apostolates = lazy(() => import('@/pages/Apostolates').then(m => ({ default: m.Apostolates })));
const ParishApostolate = lazy(() => import('@/pages/ParishApostolate').then(m => ({ default: m.ParishApostolate })));
const ParishDetails = lazy(() => import('@/pages/ParishApostolate/ParishDetails').then(m => ({ default: m.ParishDetails })));
const MediaApostolate = lazy(() => import('@/pages/MediaApostolate').then(m => ({ default: m.MediaApostolate })));
const RetreatCenters = lazy(() => import('@/pages/RetreatCenters').then(m => ({ default: m.RetreatCenters })));
const Education = lazy(() => import('@/pages/Education').then(m => ({ default: m.Education })));
const Healthcare = lazy(() => import('@/pages/Healthcare').then(m => ({ default: m.Healthcare })));
const CharitableOrganizations = lazy(() => import('@/pages/CharitableOrganizations').then(m => ({ default: m.CharitableOrganizations })));
const Vocations = lazy(() => import('@/pages/Vocations').then(m => ({ default: m.Vocations })));
const MassIntention = lazy(() => import('@/pages/MassIntention').then(m => ({ default: m.MassIntention })));
const Donation = lazy(() => import('@/pages/Donation').then(m => ({ default: m.Donation })));
const Blog = lazy(() => import('@/pages/Blog/Blog').then(m => ({ default: m.Blog })));
const BlogPostPage = lazy(() => import('@/pages/Blog/BlogPostPage').then(m => ({ default: m.BlogPostPage })));
const Contact = lazy(() => import('@/pages/Contact').then(m => ({ default: m.Contact })));
const EventsPage = lazy(() => import('@/pages/Events').then(m => ({ default: m.Events })));
const GalleryPage = lazy(() => import('@/pages/Gallery').then(m => ({ default: m.Gallery })));
const Booking = lazy(() => import('@/pages/Booking').then(m => ({ default: m.Booking })));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import('@/pages/TermsOfService').then(m => ({ default: m.TermsOfService })));
const ProjectsPage = lazy(() => import('@/pages/Projects').then(m => ({ default: m.Projects })));
const NotFound = lazy(() => import('@/pages/NotFound').then(m => ({ default: m.NotFound })));
const ServerError = lazy(() => import('@/pages/ServerError').then(m => ({ default: m.ServerError })));

// Subukia mini-site (Shrine Apostolate)
const SubukiaLayout = lazy(() => import('@/subukia/SubukiaLayout').then(m => ({ default: m.SubukiaLayout })));
const SubukiaHome = lazy(() => import('@/subukia/pages/SubukiaHome').then(m => ({ default: m.SubukiaHome })));
const SubukiaAbout = lazy(() => import('@/subukia/pages/SubukiaAbout').then(m => ({ default: m.SubukiaAbout })));
const SubukiaHistory = lazy(() => import('@/subukia/pages/SubukiaHistory').then(m => ({ default: m.SubukiaHistory })));
const SubukiaPrayers = lazy(() => import('@/subukia/pages/SubukiaPrayers').then(m => ({ default: m.SubukiaPrayers })));
const SubukiaPlaceholder = lazy(() => import('@/subukia/pages/SubukiaPlaceholder').then(m => ({ default: m.SubukiaPlaceholder })));

// Admin pages
const Dashboard = lazy(() => import('@/pages/admin/Dashboard').then(m => ({ default: m.Dashboard })));
const MassIntentions = lazy(() => import('@/pages/admin/MassIntentions').then(m => ({ default: m.MassIntentions })));
const PilgrimageBookings = lazy(() => import('@/pages/admin/PilgrimageBookings').then(m => ({ default: m.PilgrimageBookings })));
const Blogs = lazy(() => import('@/pages/admin/Blogs').then(m => ({ default: m.Blogs })));
const BlogCategories = lazy(() => import('@/pages/admin/BlogCategories').then(m => ({ default: m.BlogCategories })));
const Donations = lazy(() => import('@/pages/admin/Donations').then(m => ({ default: m.Donations })));
const Gallery = lazy(() => import('@/pages/admin/Gallery').then(m => ({ default: m.Gallery })));
import { Events } from '@/pages/admin/Events/Events';
const Notifications = lazy(() => import('@/pages/admin/Notifications').then(m => ({ default: m.Notifications })));
const AdminProjects = lazy(() => import('@/pages/admin/Projects').then(m => ({ default: m.Projects })));
const UserProfile = lazy(() => import('@/pages/admin/UserProfile').then(m => ({ default: m.UserProfile })));
const Users = lazy(() => import('@/pages/admin/Users').then(m => ({ default: m.Users })));
const Login = lazy(() => import('@/pages/admin/Login').then(m => ({ default: m.Login })));
const SetPassword = lazy(() => import('@/pages/admin/SetPassword').then(m => ({ default: m.SetPassword })));

// Loading fallback component
const LoadingFallback = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
    <div className="text-center">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="mt-3 text-muted">Loading page...</p>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <div className="App">
            <DonationPopup />
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/apostolates" element={<Apostolates />} />
                <Route path="/apostolates/parish" element={<ParishApostolate />} />
                <Route path="/apostolates/parish/:slug" element={<ParishDetails />} />
                <Route path="/apostolates/media" element={<MediaApostolate />} />
                <Route path="/apostolates/retreat-centers" element={<RetreatCenters />} />
                <Route path="/apostolates/education" element={<Education />} />
                <Route path="/apostolates/healthcare" element={<Healthcare />} />
                <Route path="/apostolates/charitable" element={<CharitableOrganizations />} />
                <Route path="/vocations" element={<Vocations />} />
                <Route path="/mass-intention" element={<MassIntention />} />
                <Route path="/donation" element={<Donation />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/post/:slug" element={<BlogPostPage />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/events" element={<EventsPage />} />
                <Route path="/gallery" element={<GalleryPage />} />
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/booking" element={<Booking />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />

                {/* Subukia mini-site (Shrine Apostolate) */}
                <Route path="/subukia" element={<SubukiaLayout />}>
                  <Route index element={<SubukiaHome />} />
                  <Route path="about" element={<SubukiaAbout />} />
                  <Route path="history" element={<SubukiaHistory />} />
                  <Route path="prayers" element={<SubukiaPrayers />} />
                  <Route path="news" element={<SubukiaPlaceholder title="News" />} />
                  <Route path="visit" element={<SubukiaPlaceholder title="Plan Your Visit" />} />
                  <Route path="shop" element={<SubukiaPlaceholder title="Shop" />} />
                  <Route path="events" element={<SubukiaPlaceholder title="Event Calendar" />} />
                  <Route path="mass-schedule" element={<SubukiaPlaceholder title="Mass Schedule" />} />
                </Route>

                {/* Admin: login and set-password (no layout) */}
                <Route path="/admin/login" element={<Login />} />
                <Route path="/admin/set-password" element={<SetPassword />} />

                {/* Admin Routes (protected) */}
                <Route path="/admin" element={<ProtectedAdminRoute><Dashboard /></ProtectedAdminRoute>} />
                <Route path="/admin/mass-intentions" element={<ProtectedAdminRoute><MassIntentions /></ProtectedAdminRoute>} />
                <Route path="/admin/pilgrimage-bookings" element={<ProtectedAdminRoute><PilgrimageBookings /></ProtectedAdminRoute>} />
                <Route path="/admin/blogs" element={<ProtectedAdminRoute><Blogs /></ProtectedAdminRoute>} />
                <Route path="/admin/blog-categories" element={<ProtectedAdminRoute><BlogCategories /></ProtectedAdminRoute>} />
                <Route path="/admin/donations" element={<ProtectedAdminRoute><Donations /></ProtectedAdminRoute>} />
                <Route path="/admin/gallery" element={<ProtectedAdminRoute><Gallery /></ProtectedAdminRoute>} />
                <Route path="/admin/events" element={<ProtectedAdminRoute><Events /></ProtectedAdminRoute>} />
                <Route path="/admin/projects" element={<ProtectedAdminRoute><AdminProjects /></ProtectedAdminRoute>} />
                <Route path="/admin/users" element={<ProtectedAdminRoute><Users /></ProtectedAdminRoute>} />
                <Route path="/admin/notifications" element={<ProtectedAdminRoute><Notifications /></ProtectedAdminRoute>} />
                <Route path="/admin/profile" element={<ProtectedAdminRoute><UserProfile /></ProtectedAdminRoute>} />

                <Route path="/error" element={<ServerError />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <ScrollToTop />
          </div>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
